/**
 * The Disk chart's breakdown: what `du` said about a directory, trimmed to the
 * largest entries. Shared so the server can check a path by the same rule the
 * bridge does.
 */
/** How long `du` may take; the server waits a little longer for the bridge. */
export const DU_TIMEOUT_MS = 180_000;

/** Entries a breakdown keeps; the rest are summed as `otherBytes`. */
export const DISK_ENTRIES = 40;

/** Why `path` is not one `du` may be asked about: absolute, normalised, nothing odd. */
export function diskPathProblem(path: unknown): string | null {
  if (typeof path !== "string" || !path) return "name a directory";
  if (path.length > 1024 || /[\0\n\r]/.test(path)) return "not a path";
  if (!path.startsWith("/")) return "the path must be absolute";
  // Normalised: no empty, "." or ".." segment, and no trailing slash but on / itself.
  if (path !== "/" && path.slice(1).split("/").some((seg) => seg === "" || seg === "." || seg === "..")) return "the path must be normalised";
  return null;
}

/**
 * `du -B1 -a --max-depth=1 <path>` output: `bytes<TAB>path` per line, the
 * directory itself last. Entries come back by name relative to `path`, biggest
 * first; whether each is a directory is the caller's to say.
 */
export function parseDu(out: string, path: string): { bytes: number; entries: { name: string; bytes: number }[] } {
  let total = 0;
  const entries: { name: string; bytes: number }[] = [];
  const prefix = path === "/" ? "/" : `${path}/`;
  for (const line of out.split("\n")) {
    const tab = line.indexOf("\t");
    if (tab < 0) continue;
    const bytes = Number(line.slice(0, tab));
    const p = line.slice(tab + 1);
    if (!Number.isFinite(bytes)) continue;
    if (p === path) total = bytes;
    else if (p.startsWith(prefix) && !p.slice(prefix.length).includes("/")) entries.push({ name: p.slice(prefix.length), bytes });
  }
  entries.sort((a, b) => b.bytes - a.bytes || a.name.localeCompare(b.name));
  return { bytes: total, entries };
}
